import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mvpApi } from '../services/api';
import LegalModal from '../components/LegalModal';
import PhoneDialNationalInput from '../applicant/components/ui/PhoneDialNationalInput';
import { splitDialNationalFromStored } from '../applicant/utils/splitDialNational';
import {
  crewFirstName,
  getCrewParticipantContext,
  persistParticipantProjectName,
  storeCrewParticipantContext,
} from '../utils/participantCrewContext';
import { countryNameFromIso2, iso2FromCountryName } from '../utils/countries';
import { formatApiDetail } from '../utils/apiError';
import { markApplicantSessionStarted } from '../utils/applicantSession';
import { getParticipantHomePath, rememberAuthPortal } from '../utils/postLoginNavigation';
import {
  applyOtpCellInput,
  emptyOtpDigits,
  normalizeOtpDigits,
  otpFocusAfterInput,
} from '../utils/otpInput';
import { isValidRequiredPhoneParts } from '../applicant/utils/contactFieldValidation';
import { loadGmContent } from '../procedures/procedureContent';
import LanguageSwitch from '../components/LanguageSwitch';
import './CustomerSignup.css';

/** Warm Personal details + KB as soon as auth exists (login / Get started). */
function prefetchParticipantPersonalDetails() {
  mvpApi.getPersonalCollection().catch(() => {});
  loadGmContent().catch(() => {});
}

const WELCOME_VIDEO_SRC = '/videos/participant-welcome.mp4';
const WELCOME_POSTER_SRC = '/videos/participant-welcome-poster.jpg';
const STEPS = {
  EMAIL: 'email',
  PROJECT: 'project',
  OTP: 'otp',
  PHONE: 'phone',
  PHONE_OTP: 'phone_otp',
  WELCOME: 'welcome',
};

const PROG = {
  [STEPS.EMAIL]: '20%',
  [STEPS.PROJECT]: '30%',
  [STEPS.OTP]: '40%',
  [STEPS.PHONE]: '65%',
  [STEPS.PHONE_OTP]: '90%',
  [STEPS.WELCOME]: '100%',
};

/** Fallbacks when the OTP API does not report a TTL (backend: email 600s, SMS 120s). */
const EMAIL_OTP_TTL_SECONDS = 600;
const PHONE_OTP_TTL_SECONDS = 120;

function ttlLabel(seconds) {
  const minutes = Math.max(1, Math.round(Number(seconds || 0) / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** "+1 415 812 8470" style display for the verify-mobile copy. */
function formatPhoneDisplay(countryCode, nationalRaw) {
  const digits = String(nationalRaw || '').replace(/\D/g, '');
  const grouped = digits.length === 10
    ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    : digits.replace(/(\d{3})(?=\d)/g, '$1 ');
  return `${countryCode || ''} ${grouped}`.trim();
}

function destinationFromProcedureSlug(slug) {
  const m = String(slug || '').trim().match(/^([a-z]{2})[-_]/i);
  if (!m) return '';
  const name = countryNameFromIso2(m[1]);
  return name === '-' ? '' : name;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function toE164(countryCode, nationalRaw) {
  const digits = String(nationalRaw || '').replace(/\D/g, '');
  if (!digits) return '';
  const ccDigits = String(countryCode || '').replace(/\D/g, '') || '1';
  return `+${ccDigits}${digits}`;
}

function splitFullName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

function fullNameFromUser(user) {
  if (!user || typeof user !== 'object') return '';
  const parts = [user.first_name, user.middle_name, user.last_name, user.second_surname]
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  return String(user.display_name || '').trim();
}

function nationalityFromUser(user) {
  if (!user || typeof user !== 'object') return '';
  const raw = String(user.country_of_origin || user.nationality || '').trim();
  if (!raw) return '';
  if (raw.length === 2) return countryNameFromIso2(raw);
  return raw;
}

function LoadingScreen() {
  return (
    <div className="psu">
      <div className="psu-step">
        <InvitationTopbar />
        <div className="psu-body psu-body--center">
          <div className="psu-spinner" aria-label="Loading" />
          <p className="psu-gate-text">Verifying your invitation…</p>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ crewInvite }) {
  return (
    <div className="psu">
      <div className="psu-step">
        <InvitationTopbar />
        <div className="psu-body psu-body--center">
          <div className="psu-error-icon" aria-hidden>
            <i className="ri-link-unlink-m" />
          </div>
          <h1 className="psu-error-title">Invitation not valid</h1>
          <p className="psu-gate-text">
            {crewInvite
              ? 'This invitation link is invalid, expired, or already used. Ask your line producer for a new one.'
              : 'This invite link is invalid or has expired. Please ask your organization for a new link.'}
          </p>
          <a href="https://globalmobility.law/" className="psu-error-home">
            Go to Global Mobility
          </a>
        </div>
      </div>
    </div>
  );
}

function InvitationTopbar() {
  return (
    <header className="psu-topbar">
      <div className="psu-brand">
        <span className="psu-brand-mark" aria-hidden>GM</span>
        <strong className="psu-brand-wordmark">GLOBAL MOBILITY</strong>
      </div>
      <LanguageSwitch enEsOnly compact className="lang-switch--fieldwork" />
    </header>
  );
}

function ProgressBar({ width }) {
  return (
    <div className="psu-prog">
      <div className="psu-progbar">
        <div className="psu-progfill" style={{ width }} />
      </div>
    </div>
  );
}

/** Inline "Privacy Policy | Terms & Conditions" links (open the legal modal). */
function LegalLinks({ onOpen, className = '' }) {
  return (
    <span className={`psu-legal-links ${className}`.trim()}>
      <button type="button" onClick={() => onOpen('privacy')}>Privacy Policy</button>
      <span className="psu-legal-sep" aria-hidden>|</span>
      <button type="button" onClick={() => onOpen('terms')}>Terms &amp; Conditions</button>
    </span>
  );
}

function StepFooter({ onOpenLegal }) {
  return (
    <footer className="psu-step-footer">
      <strong>Global Mobility</strong>
      <LegalLinks onOpen={onOpenLegal} />
    </footer>
  );
}

/** Six single-digit cells; the first cell accepts an autofilled or pasted full code. */
function OtpCells({ digits, refs, onChange, onKeyDown, onPaste, onSubmit, label }) {
  return (
    <div className="psu-otp" role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className="psu-otp-cell"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={index === 0 ? 6 : 1}
          value={digit}
          aria-label={`Digit ${index + 1}`}
          onChange={(e) => onChange(index, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
            else onKeyDown(index, e);
          }}
          onPaste={onPaste}
        />
      ))}
    </div>
  );
}

/**
 * Participant activation entry - validates ``?org=`` + ``?crew=`` magic link,
 * then email OTP signup (must match invite CSV email).
 */
export default function CustomerSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orgRefFromUrl = (searchParams.get('org') || '').trim();
  const crewTokenFromUrl = (searchParams.get('crew') || '').trim();
  const intentFromUrl = (searchParams.get('intent') || '').trim().toLowerCase();

  const [status, setStatus] = useState('loading');
  const [procedureSlug, setProcedureSlug] = useState('');
  const [step, setStep] = useState(STEPS.EMAIL);
  const [authIntent, setAuthIntent] = useState(
    intentFromUrl === 'login' ? 'login' : 'signup',
  );
  const [orgRef, setOrgRef] = useState(orgRefFromUrl);
  const [crewToken, setCrewToken] = useState(crewTokenFromUrl);
  const [projectChoices, setProjectChoices] = useState([]);

  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(() => emptyOtpDigits());
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const [fullName, setFullName] = useState('');
  const [nationality, setNationality] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [phoneNational, setPhoneNational] = useState('');
  /** SMS consent (Telnyx/TCPA): required before any verification SMS is sent. */
  const [smsConsent, setSmsConsent] = useState(false);
  /** TTL of the code currently awaiting entry (email or SMS), from the API when reported. */
  const [otpTtlSeconds, setOtpTtlSeconds] = useState(EMAIL_OTP_TTL_SECONDS);

  const [videoPlaying, setVideoPlaying] = useState(false);
  const [openLegal, setOpenLegal] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [orgName, setOrgName] = useState('');

  const otpRefs = useRef([]);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!orgRefFromUrl) {
      // Email-first entry (no magic link): allow picking a production after email.
      setStatus('ready');
      return undefined;
    }

    let cancelled = false;

    async function validate() {
      try {
        const orgRes = await mvpApi.getOrgByCustomerRef(orgRefFromUrl);
        if (cancelled) return;
        let resolvedSlug = orgRes.data?.procedure_slug || '';
        let resolvedOrgName = orgRes.data?.organization_name || '';
        let project = orgRes.data?.project_name || '';
        let crewName = '';
        let crewEmail = '';
        let crewPhone = '';
        let jobTitle = '';
        let department = '';
        let country = '';
        let dataNotice = '';
        let scheduleStart = '';
        let scheduleEnd = '';

        if (crewTokenFromUrl) {
          const crewRes = await mvpApi.getCrewInvite(crewTokenFromUrl, orgRefFromUrl);
          if (cancelled) return;
          const crew = crewRes.data || {};
          if (crew.procedure_slug) resolvedSlug = crew.procedure_slug;
          crewName = crew.prefilled_name || '';
          crewEmail = crew.prefilled_email || '';
          crewPhone = crew.prefilled_phone || '';
          jobTitle = crew.prefilled_job_title || '';
          department = crew.prefilled_department || '';
          country = crew.prefilled_country || '';
          if (crew.project_name) project = crew.project_name;
          dataNotice = crew.data_notice || '';
          scheduleStart = crew.schedule_start_date || '';
          scheduleEnd = crew.schedule_end_date || '';
        }

        storeCrewParticipantContext({
          orgName: resolvedOrgName,
          orgRef: orgRefFromUrl,
          procedureSlug: resolvedSlug,
          crewToken: crewTokenFromUrl || '',
          crewName,
          crewEmail,
          crewPhone,
          jobTitle,
          department,
          country,
          projectName: project,
          dataNotice,
          scheduleStart,
          scheduleEnd,
        });

        setProcedureSlug(resolvedSlug);
        setProjectName(project);
        setOrgName(resolvedOrgName);
        if (project) persistParticipantProjectName(project);
        setEmail(crewEmail);
        setFullName(crewName);
        setNationality(country ? countryNameFromIso2(country) : '');
        if (crewPhone) {
          const split = splitDialNationalFromStored(crewPhone, '+1');
          setPhoneCountryCode(split.dial || '+1');
          setPhoneNational(String(split.national || '').replace(/\D/g, ''));
        }
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    validate();
    return () => {
      cancelled = true;
    };
  }, [orgRefFromUrl, crewTokenFromUrl]);

  useEffect(() => {
    if (otpResendTimer <= 0) return undefined;
    const id = setTimeout(() => setOtpResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [otpResendTimer]);

  const firstName = useMemo(() => crewFirstName(fullName) || 'there', [fullName]);
  const destination = useMemo(
    () => destinationFromProcedureSlug(procedureSlug),
    [procedureSlug],
  );
  const productionLabel = projectName || 'your production';
  const otpCode = otpDigits.join('');
  const browserLanguage = useMemo(() => {
    try {
      return (navigator.language || '').slice(0, 16) || null;
    } catch {
      return null;
    }
  }, []);

  const sendEmailOtp = useCallback(async (selectedOrgRef, selectedCrewToken) => {
    const res = await mvpApi.requestParticipantEmailOtp(
      email.trim(),
      selectedOrgRef,
      selectedCrewToken || null,
      authIntent,
      'email',
    );
    const data = res.data || {};
    setMaskedEmail(data.masked_destination || email.trim());
    setOtpTtlSeconds(Number(data.ttl_seconds) || EMAIL_OTP_TTL_SECONDS);
    setOtpDigits(emptyOtpDigits());
    setOtpResendTimer(30);
    setStep(STEPS.OTP);
    window.setTimeout(() => otpRefs.current[0]?.focus?.(), 0);
  }, [email, authIntent]);

  const applySelectedProject = useCallback(async (choice) => {
    const nextOrg = (choice.customer_signup_ref || '').trim();
    const nextCrew = (choice.crew_invite_token || '').trim();
    if (!nextOrg) {
      throw new Error('This production is missing a signup link. Ask your producer for help.');
    }
    setOrgRef(nextOrg);
    setCrewToken(nextCrew);
    setProjectName(choice.project_name || '');
    if (choice.project_name) persistParticipantProjectName(choice.project_name);

    try {
      const orgRes = await mvpApi.getOrgByCustomerRef(nextOrg);
      const resolvedSlug = orgRes.data?.procedure_slug || '';
      setProcedureSlug(resolvedSlug);
      const resolvedOrgName = orgRes.data?.organization_name || choice.organization_name || '';
      setOrgName(resolvedOrgName);
      storeCrewParticipantContext({
        orgName: resolvedOrgName,
        orgRef: nextOrg,
        procedureSlug: resolvedSlug,
        crewToken: nextCrew,
        crewName: fullName || '',
        crewEmail: email.trim(),
        crewPhone: '',
        jobTitle: '',
        department: '',
        country: '',
        projectName: choice.project_name || orgRes.data?.project_name || '',
        dataNotice: '',
        scheduleStart: '',
        scheduleEnd: '',
      });
      if (nextCrew) {
        try {
          const crewRes = await mvpApi.getCrewInvite(nextCrew, nextOrg);
          const crew = crewRes.data || {};
          if (crew.prefilled_phone) {
            const split = splitDialNationalFromStored(crew.prefilled_phone, '+1');
            setPhoneCountryCode(split.dial || '+1');
            setPhoneNational(String(split.national || '').replace(/\D/g, ''));
          }
          if (crew.prefilled_name) setFullName(crew.prefilled_name);
          if (crew.prefilled_country) {
            setNationality(countryNameFromIso2(crew.prefilled_country));
          }
          storeCrewParticipantContext({
            orgName: orgRes.data?.organization_name || choice.organization_name || '',
            orgRef: nextOrg,
            procedureSlug: crew.procedure_slug || resolvedSlug,
            crewToken: nextCrew,
            crewName: crew.prefilled_name || fullName || '',
            crewEmail: crew.prefilled_email || email.trim(),
            crewPhone: crew.prefilled_phone || '',
            jobTitle: crew.prefilled_job_title || '',
            department: crew.prefilled_department || '',
            country: crew.prefilled_country || '',
            projectName: crew.project_name || choice.project_name || '',
            dataNotice: crew.data_notice || '',
            scheduleStart: crew.schedule_start_date || '',
            scheduleEnd: crew.schedule_end_date || '',
          });
        } catch {
          // org context is enough to continue OTP
        }
      }
    } catch {
      // Still attempt OTP with selected refs
    }

    await sendEmailOtp(nextOrg, nextCrew);
  }, [email, fullName, sendEmailOtp]);

  const handleContinueFromEmail = useCallback(async () => {
    setFormError('');
    if (!isValidEmail(email)) {
      setFormError('Enter a valid email address.');
      return;
    }

    setBusy(true);
    try {
      // Signup is always scoped by the production invite URL (?org=…).
      // Multi-production choice is only needed on login.
      if (authIntent !== 'login') {
        if (!orgRef) {
          setFormError('Open your production invite link to create an account.');
          return;
        }
        await sendEmailOtp(orgRef, crewToken || null);
        return;
      }

      const res = await mvpApi.listParticipantProjects(email.trim(), 'login');
      const projects = res.data?.projects || [];
      if (!projects.length) {
        setFormError(
          'No registered production found for this email. Create an account from your invite link first.',
        );
        return;
      }

      if (projects.length === 1) {
        await applySelectedProject(projects[0]);
        return;
      }

      setProjectChoices(projects);
      setStep(STEPS.PROJECT);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not continue. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [email, authIntent, orgRef, crewToken, sendEmailOtp, applySelectedProject]);

  const handleSelectProject = useCallback(async (choice) => {
    setFormError('');
    setBusy(true);
    try {
      await applySelectedProject(choice);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not send the code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [applySelectedProject]);

  const handleResend = useCallback(async () => {
    if (otpResendTimer > 0 || busy) return;
    setFormError('');
    setBusy(true);
    try {
      const res = await mvpApi.requestParticipantEmailOtp(
        email.trim(),
        orgRef,
        crewToken || null,
        authIntent,
        'email',
      );
      setOtpTtlSeconds(Number(res?.data?.ttl_seconds) || EMAIL_OTP_TTL_SECONDS);
      setOtpDigits(emptyOtpDigits());
      setOtpResendTimer(30);
      window.setTimeout(() => otpRefs.current[0]?.focus?.(), 0);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not resend the code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [otpResendTimer, busy, email, orgRef, crewToken, authIntent]);

  const handleVerifyOtp = useCallback(async () => {
    setFormError('');
    if (otpCode.length !== 6) {
      setFormError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    try {
      const res = await mvpApi.verifyParticipantEmailOtp(
        email.trim(),
        otpCode,
        orgRef,
        crewToken || null,
        authIntent,
        browserLanguage,
        'email',
      );
      const data = res.data || {};
      if (!data.token) {
        setFormError('Invalid response from server. Please try again.');
        return;
      }
      localStorage.setItem('authToken', data.token);
      if (data.user) localStorage.setItem('userData', JSON.stringify(data.user));
      markApplicantSessionStarted();
      rememberAuthPortal('email', data.user);
      sessionStorage.setItem('mvp_applicant_entry_intent', authIntent);
      prefetchParticipantPersonalDetails();

      if (authIntent === 'login') {
        navigate(getParticipantHomePath(), { replace: true });
        return;
      }

      // Prefill the mobile step from the users table (+ invite-applied profile on the user).
      const user = data.user || {};
      const crew = getCrewParticipantContext();
      const nameFromUser = fullNameFromUser(user);
      const nameFromCrew = (crew?.name || '').trim();
      setFullName((prev) => nameFromUser || nameFromCrew || prev || '');
      const nextEmail = ((user.email || '').trim() || email.trim()).toLowerCase();
      setEmail(nextEmail);
      const storedPhone = (user.phone || '').trim() || (crew?.phone || '').trim();
      if (storedPhone) {
        const split = splitDialNationalFromStored(storedPhone, phoneCountryCode || '+1');
        setPhoneCountryCode(split.dial || '+1');
        setPhoneNational(String(split.national || '').replace(/\D/g, ''));
      } else {
        setPhoneNational('');
      }
      const natFromUser = nationalityFromUser(user);
      const natFromCrew = crew?.country ? countryNameFromIso2(crew.country) : '';
      setNationality((prev) => natFromUser || natFromCrew || prev || '');
      setOtpDigits(emptyOtpDigits());
      setSmsConsent(false);

      setStep(STEPS.PHONE);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Invalid or expired code.',
      );
    } finally {
      setBusy(false);
    }
  }, [otpCode, email, orgRef, crewToken, authIntent, browserLanguage, phoneCountryCode, navigate]);

  /**
   * Persist invite-derived profile fields (and the mobile number once verified),
   * then move to the welcome screen. ``verifiedPhone`` is the E.164 number that
   * just passed SMS OTP, or empty when the participant skipped.
   */
  const finishRegistration = useCallback(async (verifiedPhone = '') => {
    const trimmedEmail = String(email || '').trim().toLowerCase();
    const { first_name, last_name } = splitFullName(fullName);
    const iso2 = iso2FromCountryName(nationality);

    if (first_name) {
      const payload = {
        first_name,
        last_name: last_name || first_name,
        email: trimmedEmail,
        consent_updates: Boolean(verifiedPhone && smsConsent),
      };
      if (iso2) payload.country_of_origin = iso2;
      if (verifiedPhone) payload.phone = verifiedPhone;

      const res = await mvpApi.updatePersonalDetails(payload);
      const user = res?.data?.user;
      if (user) {
        try {
          localStorage.setItem('userData', JSON.stringify(user));
        } catch {
          /* ignore */
        }
      }
    }
    persistParticipantProjectName(projectName);
    prefetchParticipantPersonalDetails();
    setStep(STEPS.WELCOME);
  }, [email, fullName, nationality, projectName, smsConsent]);

  const phoneE164 = toE164(phoneCountryCode, phoneNational);
  const phoneValid = isValidRequiredPhoneParts(phoneCountryCode, phoneNational);

  /** "Send verification code": requires explicit SMS consent before any message is sent. */
  const handleSendPhoneCode = useCallback(async () => {
    setFormError('');
    if (!smsConsent) {
      setFormError('Agree to receive SMS messages to continue, or skip for now.');
      return;
    }
    if (!phoneValid) {
      setFormError('Enter a valid mobile number for the selected country.');
      return;
    }
    setBusy(true);
    try {
      const res = await mvpApi.requestProfilePhoneOtp(phoneE164, 'sms');
      setOtpTtlSeconds(Number(res?.data?.ttl_seconds) || PHONE_OTP_TTL_SECONDS);
      setOtpDigits(emptyOtpDigits());
      setOtpResendTimer(30);
      setStep(STEPS.PHONE_OTP);
      window.setTimeout(() => otpRefs.current[0]?.focus?.(), 0);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not send the code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [smsConsent, phoneValid, phoneE164]);

  const handleSkipPhone = useCallback(async () => {
    setFormError('');
    setBusy(true);
    try {
      await finishRegistration('');
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not continue. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [finishRegistration]);

  const handleVerifyPhoneOtp = useCallback(async () => {
    setFormError('');
    if (otpCode.length !== 6) {
      setFormError('Enter the 6-digit code from the text message.');
      return;
    }
    setBusy(true);
    try {
      const res = await mvpApi.verifyProfilePhoneOtp(phoneE164, otpCode);
      const user = res?.data?.user;
      if (user) {
        try {
          localStorage.setItem('userData', JSON.stringify(user));
        } catch {
          /* ignore */
        }
      }
      setOtpDigits(emptyOtpDigits());
      await finishRegistration(phoneE164);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Invalid or expired code.',
      );
    } finally {
      setBusy(false);
    }
  }, [otpCode, phoneE164, finishRegistration]);

  const handleResendPhoneOtp = useCallback(async () => {
    if (otpResendTimer > 0 || busy) return;
    setFormError('');
    setBusy(true);
    try {
      const res = await mvpApi.requestProfilePhoneOtp(phoneE164, 'sms');
      setOtpTtlSeconds(Number(res?.data?.ttl_seconds) || PHONE_OTP_TTL_SECONDS);
      setOtpDigits(emptyOtpDigits());
      setOtpResendTimer(30);
      window.setTimeout(() => otpRefs.current[0]?.focus?.(), 0);
    } catch (err) {
      setFormError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not resend the code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [otpResendTimer, busy, phoneE164]);

  const handleOtpChange = (index, raw) => {
    setOtpDigits((prev) => applyOtpCellInput(prev, index, raw));
    const focusAt = otpFocusAfterInput(index, raw);
    // Defer focus so React can commit the multi-digit autofill value first.
    window.requestAnimationFrame(() => {
      otpRefs.current[focusAt]?.focus();
    });
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    const digits = normalizeOtpDigits(pasted);
    if (!digits.length) return;
    e.preventDefault();
    setOtpDigits(applyOtpCellInput(emptyOtpDigits(), 0, digits.join('')));
    const focusAt = Math.min(digits.length, 5);
    window.requestAnimationFrame(() => {
      otpRefs.current[focusAt]?.focus();
    });
  };

  const playWelcomeVideo = () => {
    setVideoPlaying(true);
    const el = videoRef.current;
    if (el) {
      el.controls = true;
      el.play()?.catch(() => {});
    }
  };

  useEffect(() => {
    if (step === STEPS.WELCOME) {
      window.scrollTo(0, 0);
      const el = videoRef.current;
      if (el) {
        const promise = el.play();
        if (promise !== undefined) {
          promise
            .then(() => {
              el.controls = true;
              setVideoPlaying(true);
            })
            .catch(() => {
              // Browser policy prevented unmuted autoplay; user clicks play button
            });
        }
      }
    }
  }, [step]);

  const handleGetStarted = () => {
    setVideoPlaying(false);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {
        /* ignore */
      }
    }
    prefetchParticipantPersonalDetails();
    navigate(getParticipantHomePath());
  };

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'error') return <ErrorScreen crewInvite={Boolean(crewTokenFromUrl)} />;

  if (step === STEPS.WELCOME) {
    return (
      <div className="psu">
        <div className="psu-welcome">
          <div
            className={`psu-wel-stage${videoPlaying ? ' playing' : ''}`}
            onClick={!videoPlaying ? playWelcomeVideo : undefined}
            onKeyDown={
              !videoPlaying
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      playWelcomeVideo();
                    }
                  }
                : undefined
            }
            role={!videoPlaying ? 'button' : undefined}
            tabIndex={!videoPlaying ? 0 : undefined}
            aria-label={!videoPlaying ? 'Play welcome video' : undefined}
          >
            <img
              className={`psu-wel-poster${videoPlaying ? ' is-hidden' : ''}`}
              src={WELCOME_POSTER_SRC}
              alt=""
              aria-hidden
            />
            <video
              ref={videoRef}
              className={`psu-wel-video${videoPlaying ? ' is-playing' : ''}`}
              src={WELCOME_VIDEO_SRC}
              poster={WELCOME_POSTER_SRC}
              playsInline
              preload="metadata"
              onPlay={() => setVideoPlaying(true)}
            />
            <div className="psu-wel-vlabel">
              <span className="psu-logo" style={{ color: 'rgba(255,255,255,0.92)' }}>
                GLOBAL MOBILITY
              </span>
            </div>
            <button type="button" className="psu-wel-play" onClick={playWelcomeVideo} aria-label="Play">
              <svg width="22" height="26" viewBox="0 0 22 26" fill="none" aria-hidden>
                <path d="M0 0L22 13L0 26V0Z" fill="white" />
              </svg>
            </button>
          </div>

          <div className="psu-wel-sheet">
            <div className="psu-wel-take">SCENE 1 · TAKE 1</div>
            <div className="psu-wel-h1">
              Welcome to {productionLabel},
              <br />
              <span className="nm">{firstName}</span>
            </div>
            <div className="psu-wel-sub">
              {destination ? (
                <>
                  We get you to <span className="psu-wel-loc">{destination}</span> ready to work, on
                  time and fully cleared.
                </>
              ) : (
                <>We get you ready to work, on time and fully cleared.</>
              )}
            </div>
            <div className="psu-wel-cta">
              <button type="button" className="psu-btn-warm" onClick={handleGetStarted}>
                Get started
              </button>
              <div className="psu-wel-note">
                <span className="psu-wel-lock" aria-hidden>
                  🔒
                </span>
                Private, encrypted, and secure. GDPR compliant.
              </div>
              <div className="psu-wel-legal">
                <button type="button" onClick={() => setOpenLegal('privacy')}>
                  Privacy Policy
                </button>
                <span className="sep" aria-hidden />
                <button type="button" onClick={() => setOpenLegal('terms')}>
                  Terms
                </button>
              </div>
            </div>
          </div>
        </div>
        <LegalModal
          type={openLegal || 'terms'}
          open={openLegal !== null}
          onClose={() => setOpenLegal(null)}
        />
      </div>
    );
  }

  return (
    <div className="psu" translate="no">
      <div className="psu-step">
        <InvitationTopbar />
        <ProgressBar width={PROG[step]} />

        {step === STEPS.EMAIL && (
          <>
            <div className="psu-body psu-body--register">
              <h1 className="psu-h">
                {authIntent === 'login' ? 'Sign in' : `Join ${productionLabel}`}
              </h1>
              <p className="psu-intro">
                {authIntent === 'login' ? (
                  <>
                    Use the email on your production account. If you work on more than one
                    production, we&apos;ll ask which one to open.
                  </>
                ) : (
                  <>
                    <strong>{orgName || 'Your production'}</strong>
                    {' '}
                    has invited you to register with Global Mobility.
                  </>
                )}
              </p>
              <div className="psu-email-form">
                <label className="psu-field-label" htmlFor="psu-email">
                  Email
                </label>
                <input
                  id="psu-email"
                  className={`psu-input${formError ? ' psu-input--invalid' : ''}`}
                  type="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFormError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleContinueFromEmail();
                  }}
                />
                <p className="psu-field-hint">
                  We&apos;ll send a six-digit verification code from noreply@globalmobility.law.
                </p>
              </div>
              {formError ? <p className="psu-error">{formError}</p> : null}
              <button
                type="button"
                className="psu-next"
                onClick={handleContinueFromEmail}
                disabled={busy || !email.trim()}
              >
                {busy ? 'Checking…' : 'Continue'}
              </button>
              {authIntent === 'signup' ? (
                <button
                  type="button"
                  className="psu-signin-link"
                  onClick={() => {
                    setAuthIntent('login');
                    setFormError('');
                  }}
                >
                  Already registered? Sign in
                </button>
              ) : (
                <button
                  type="button"
                  className="psu-signin-link"
                  onClick={() => {
                    setAuthIntent('signup');
                    setFormError('');
                  }}
                >
                  Need to register? Use your invite link
                </button>
              )}
            </div>
          </>
        )}

        {step === STEPS.PROJECT && (
          <>
            <div className="psu-body psu-body--register">
              <h1 className="psu-h">Which production?</h1>
              <p className="psu-intro">
                This email is registered on more than one production. Choose where you want to continue.
              </p>
              <div className="psu-ch-list" role="list">
                {projectChoices.map((choice) => (
                  <button
                    key={choice.project_id}
                    type="button"
                    className="psu-ch"
                    role="listitem"
                    disabled={busy}
                    onClick={() => handleSelectProject(choice)}
                  >
                    <div className="psu-ch-ic" aria-hidden>🎬</div>
                    <div className="psu-ch-tx">
                      <div className="psu-ch-l">{choice.project_name}</div>
                      <div className="psu-ch-v">
                        {[choice.organization_name, choice.status === 'registered' ? 'registered' : 'invited']
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <span className="psu-ch-radio" aria-hidden />
                  </button>
                ))}
              </div>
              {formError ? <p className="psu-error">{formError}</p> : null}
              <p className="psu-intro" style={{ marginTop: 16, marginBottom: 0 }}>
                <button
                  type="button"
                  className="psu-change"
                  onClick={() => {
                    setStep(STEPS.EMAIL);
                    setProjectChoices([]);
                    setFormError('');
                  }}
                >
                  Change email
                </button>
              </p>
              <button type="button" className="psu-next" disabled style={{ marginTop: 24 }}>
                {busy ? 'Sending…' : 'Select a production'}
              </button>
            </div>
          </>
        )}

        {step === STEPS.OTP && (
          <>
            <div className="psu-body psu-body--register">
              <button
                type="button"
                className="psu-back"
                onClick={() => {
                  setStep(STEPS.EMAIL);
                  setFormError('');
                }}
              >
                ← Back
              </button>
              <h1 className="psu-h">Verify your email</h1>
              <p className="psu-intro">
                Enter the 6-digit code we sent to{' '}
                <strong>{maskedEmail || email.trim()}</strong>.
              </p>
              <OtpCells
                label="Email verification code"
                digits={otpDigits}
                refs={otpRefs}
                onChange={handleOtpChange}
                onKeyDown={handleOtpKeyDown}
                onPaste={handleOtpPaste}
                onSubmit={() => {
                  if (otpCode.length === 6) handleVerifyOtp();
                }}
              />
              <p className="psu-code-help">
                The code expires in {ttlLabel(otpTtlSeconds)}. You can request another code if
                it does not arrive.
              </p>
              {formError ? <p className="psu-error">{formError}</p> : null}
              <button
                type="button"
                className="psu-next"
                onClick={handleVerifyOtp}
                disabled={busy || otpCode.length !== 6}
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
              <p className="psu-otp-resend">
                Didn&apos;t receive a code?{' '}
                {otpResendTimer > 0 ? (
                  <span>Resend in {otpResendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    className="psu-otp-resendlink"
                    onClick={handleResend}
                    disabled={busy}
                  >
                    Resend
                  </button>
                )}
              </p>
            </div>
          </>
        )}

        {step === STEPS.PHONE && (
          <>
            <div className="psu-body psu-body--register">
              <p className="psu-verified-badge">
                <span className="psu-verified-badge-icon" aria-hidden>✓</span>
                Email verified
                {' '}
                <span className="psu-verified-badge-email">{maskedEmail || email.trim()}</span>
              </p>
              <h1 className="psu-h">Add your mobile number</h1>
              <p className="psu-intro">
                Add and verify your mobile number to receive important updates from Global
                Mobility.
              </p>

              <div className="psu-reg-fields">
                <div className="psu-reg-field psu-reg-field--wide">
                  <span>Mobile number</span>
                  <div className="psu-reg-phone">
                    <PhoneDialNationalInput
                      theme="light"
                      countryCode={phoneCountryCode}
                      onCountryCodeChange={(next) => {
                        setPhoneCountryCode(next);
                        setFormError('');
                      }}
                      nationalNumber={phoneNational}
                      onNationalNumberChange={(next) => {
                        setPhoneNational(next);
                        setFormError('');
                      }}
                      placeholder="Mobile number"
                      showFilledState={false}
                    />
                  </div>
                </div>

                <label className="psu-reg-consent psu-sms-consent">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => {
                      setSmsConsent(e.target.checked);
                      setFormError('');
                    }}
                  />
                  <span>
                    I agree to receive SMS authentication codes, account notifications, and
                    application updates from Global Mobility. Message and data rates may apply.
                    Reply STOP to opt out.
                  </span>
                </label>
                <LegalLinks onOpen={setOpenLegal} className="psu-consent-links" />
              </div>

              {formError ? <p className="psu-error">{formError}</p> : null}
              <button
                type="button"
                className="psu-next"
                onClick={handleSendPhoneCode}
                disabled={busy || !smsConsent || !phoneValid}
              >
                {busy ? 'Sending…' : 'Send verification code'}
              </button>
              <button
                type="button"
                className="psu-signin-link psu-skip-link"
                onClick={handleSkipPhone}
                disabled={busy}
              >
                Skip for now
              </button>
            </div>
          </>
        )}

        {step === STEPS.PHONE_OTP && (
          <>
            <div className="psu-body psu-body--register">
              <button
                type="button"
                className="psu-back"
                onClick={() => {
                  setStep(STEPS.PHONE);
                  setFormError('');
                  setOtpDigits(emptyOtpDigits());
                }}
              >
                ← Back
              </button>
              <h1 className="psu-h">Verify your mobile number</h1>
              <p className="psu-intro">
                Enter the 6-digit code we sent to{' '}
                <strong>{formatPhoneDisplay(phoneCountryCode, phoneNational)}</strong>.{' '}
                <button
                  type="button"
                  className="psu-edit-number"
                  onClick={() => {
                    setStep(STEPS.PHONE);
                    setFormError('');
                    setOtpDigits(emptyOtpDigits());
                  }}
                >
                  Edit number
                </button>
              </p>
              <OtpCells
                label="Mobile verification code"
                digits={otpDigits}
                refs={otpRefs}
                onChange={handleOtpChange}
                onKeyDown={handleOtpKeyDown}
                onPaste={handleOtpPaste}
                onSubmit={() => {
                  if (otpCode.length === 6) handleVerifyPhoneOtp();
                }}
              />
              <p className="psu-code-help">
                The code expires in {ttlLabel(otpTtlSeconds)}. You can request another code if
                it does not arrive.
              </p>
              {formError ? <p className="psu-error">{formError}</p> : null}
              <button
                type="button"
                className="psu-next"
                onClick={handleVerifyPhoneOtp}
                disabled={busy || otpCode.length !== 6}
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
              <p className="psu-otp-resend">
                Didn&apos;t receive a code?{' '}
                {otpResendTimer > 0 ? (
                  <span>Resend in {otpResendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    className="psu-otp-resendlink"
                    onClick={handleResendPhoneOtp}
                    disabled={busy}
                  >
                    Resend
                  </button>
                )}
              </p>
            </div>
          </>
        )}

        <StepFooter onOpenLegal={setOpenLegal} />
      </div>
      <LegalModal
        type={openLegal || 'terms'}
        open={openLegal !== null}
        onClose={() => setOpenLegal(null)}
      />
    </div>
  );
}
